import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import DiscotechLogo from "../../img/discotech_logo_white.png";
import "./DiscotechCredit.css";

const STUDIO_URL = "https://t.me/discotg/";

/* ───────────────────────── Диско-шар (canvas, настоящая 3D-проекция) ───────────────────────── */

const LAT = 9;   // поясов по широте
const LON = 18;  // граней по долготе
const TAU = Math.PI * 2;
const seed = Array.from({ length: LAT * LON }, (_, k) => {
  const x = Math.sin(k * 127.1 + 311.7) * 43758.5453;
  return x - Math.floor(x);
});
const norm = (v) => { const l = Math.hypot(...v); return v.map((c) => c / l); };
const LIGHT = norm([-0.45, 0.6, 0.7]);
const HALF = norm([LIGHT[0], LIGHT[1], LIGHT[2] + 1]); // half-vector для бликов (зритель смотрит по +z)
const IDLE_SPEED = 0;    // в покое шар стоит
const HOVER_SPEED = 2.4; // рад/с при наведении

const DiscoBall = forwardRef(function DiscoBall({ size = 18 }, ref) {
  const canvasRef = useRef(null);
  const st = useRef({ phase: 0.35, vel: 0, target: IDLE_SPEED, raf: 0, last: 0 });

  const draw = useCallback(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    const dpr = Math.min(window.devicePixelRatio || 1, 3);
    if (c.width !== size * dpr) { c.width = size * dpr; c.height = size * dpr; }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, size, size);

    const r = size / 2 - 0.5, cx = size / 2, cy = size / 2;
    const { phase } = st.current;
    const step = TAU / LON;
    const pt = (la, lo) => [cx + r * Math.cos(la) * Math.sin(lo), cy - r * Math.sin(la)];

    ctx.save();
    ctx.beginPath(); ctx.arc(cx, cy, r, 0, TAU);
    ctx.fillStyle = "#1c1c1c"; ctx.fill(); ctx.clip();

    for (let i = 0; i < LAT; i++) {
      const la0 = -Math.PI / 2 + (i * Math.PI) / LAT;
      const la1 = la0 + Math.PI / LAT;
      const lam = (la0 + la1) / 2;
      for (let j = 0; j < LON; j++) {
        let lo0 = ((j * step + phase + Math.PI) % TAU + TAU) % TAU - Math.PI;
        let lo1 = lo0 + step;
        if (lo1 < -Math.PI / 2 || lo0 > Math.PI / 2) continue; // задняя сторона
        const lom = (lo0 + lo1) / 2;
        lo0 = Math.max(lo0, -Math.PI / 2); lo1 = Math.min(lo1, Math.PI / 2);

        const n = [Math.cos(lam) * Math.sin(lom), Math.sin(lam), Math.cos(lam) * Math.cos(lom)];
        const s = seed[i * LON + j];
        const diff = Math.max(0, n[0] * LIGHT[0] + n[1] * LIGHT[1] + n[2] * LIGHT[2]);
        const spec = Math.pow(Math.max(0, n[0] * HALF[0] + n[1] * HALF[1] + n[2] * HALF[2]), 30 + s * 60);
        let g = 40 + 150 * diff + 55 * s;
        let fill;
        if (spec > 0.45) {
          // зеркальная грань ловит свет: белая вспышка с лёгким радужным отливом
          fill = `hsl(${Math.round(s * 360)}, 90%, ${Math.min(97, 75 + spec * 25)}%)`;
        } else {
          g = Math.min(235, g + spec * 160);
          fill = `rgb(${g | 0},${g | 0},${(g + 6) | 0})`;
        }

        const a = pt(la0, lo0), b = pt(la0, lo1), c2 = pt(la1, lo1), d = pt(la1, lo0);
        ctx.beginPath();
        ctx.moveTo(a[0], a[1]); ctx.lineTo(b[0], b[1]); ctx.lineTo(c2[0], c2[1]); ctx.lineTo(d[0], d[1]);
        ctx.closePath();
        ctx.fillStyle = fill; ctx.fill();
        ctx.strokeStyle = "rgba(8,8,8,.55)"; ctx.lineWidth = 0.45; ctx.stroke();
      }
    }

    // объём: затемнение к краю сферы
    const vol = ctx.createRadialGradient(cx - r * 0.35, cy - r * 0.4, r * 0.1, cx, cy, r);
    vol.addColorStop(0, "rgba(255,255,255,0.10)");
    vol.addColorStop(0.65, "rgba(0,0,0,0)");
    vol.addColorStop(1, "rgba(0,0,0,0.55)");
    ctx.fillStyle = vol; ctx.fillRect(0, 0, size, size);
    ctx.restore();

    ctx.beginPath(); ctx.arc(cx, cy, r, 0, TAU);
    ctx.strokeStyle = "rgba(255,255,255,.18)"; ctx.lineWidth = 0.6; ctx.stroke();
  }, [size]);

  const loop = useCallback((t) => {
    const s = st.current;
    const dt = Math.min(0.05, (t - (s.last || t)) / 1000);
    s.last = t;
    s.vel += (s.target - s.vel) * Math.min(1, dt * 3.5); // плавный разгон/торможение
    s.phase += s.vel * dt;
    draw();
    if (Math.abs(s.vel) > 0.01 || s.target !== 0) s.raf = requestAnimationFrame(loop);
    else { s.raf = 0; s.last = 0; }
  }, [draw]);

  const wake = useCallback(() => {
    if (!st.current.raf) st.current.raf = requestAnimationFrame(loop);
  }, [loop]);

  useImperativeHandle(ref, () => ({
    spin(on) { st.current.target = on ? HOVER_SPEED : IDLE_SPEED; wake(); },
    burst() { st.current.vel = 16; wake(); },
  }), [wake]);

  useEffect(() => {
    draw();
    const s = st.current;
    return () => cancelAnimationFrame(s.raf);
  }, [draw]);

  return <canvas ref={canvasRef} className="dt-credit__ball" style={{ width: size, height: size }} aria-hidden="true" />;
});

/* ───────────────────────── Дискотека по всему футеру ───────────────────────── */

const PALETTE = ["#ff3b7f", "#ffc23b", "#3be8ff", "#8a5cff", "#4dff9a", "#ff6ad5", "#ffffff"];
const rnd = (a, b) => a + Math.random() * (b - a);
const pick = (arr) => arr[(Math.random() * arr.length) | 0];
let activeParty = null;

function launchParty(originEl) {
  if (!originEl) return;
  activeParty?.();

  const host = originEl.closest("footer") || document.body;
  if (getComputedStyle(host).position === "static") host.style.position = "relative";

  const canvas = document.createElement("canvas");
  canvas.className = "dt-party";
  host.appendChild(canvas);
  const ctx = canvas.getContext("2d");

  const hr = host.getBoundingClientRect();
  const br = originEl.getBoundingClientRect();
  const W = hr.width, H = hr.height;
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  canvas.width = W * dpr; canvas.height = H * dpr;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

  const ox = br.left + br.width / 2 - hr.left;
  const oy = br.top + br.height / 2 - hr.top;
  const reach = Math.hypot(Math.max(ox, W - ox), Math.max(oy, H - oy));

  // отражения шара — «зайчики», которые плывут по футеру по кругу
  const spots = Array.from({ length: Math.min(140, Math.round((W * H) / 4500)) }, () => ({
    a: rnd(0, TAU), d: rnd(24, reach), s: rnd(1.5, 4.5), c: pick(PALETTE),
    tw: rnd(0, TAU), born: rnd(0, 0.45),
  }));
  // вылетающие из шара цветные шарики
  const sparks = Array.from({ length: 34 }, () => {
    const a = rnd(0, TAU), v = rnd(120, 420);
    return { x: ox, y: oy, vx: Math.cos(a) * v, vy: Math.sin(a) * v - 60, r: rnd(1.5, 3.5), c: pick(PALETTE), life: rnd(0.9, 1.6) };
  });
  const BEAMS = 7;
  const DUR = 3.4;
  const start = performance.now();
  let last = start, raf = 0;

  const stop = () => {
    cancelAnimationFrame(raf);
    canvas.remove();
    if (activeParty === stop) activeParty = null;
  };
  activeParty = stop;

  const frame = (now) => {
    const t = (now - start) / 1000;
    const dt = Math.min(0.05, (now - last) / 1000);
    last = now;
    if (t > DUR) return stop();
    const env = t < 0.2 ? t / 0.2 : t > DUR - 1.1 ? Math.max(0, (DUR - t) / 1.1) : 1;

    ctx.clearRect(0, 0, W, H);
    ctx.globalCompositeOperation = "lighter";

    // 1. лучи прожекторов из шара
    for (let k = 0; k < BEAMS; k++) {
      const ang = (k * TAU) / BEAMS + t * 0.8;
      const spread = 0.05 + 0.02 * Math.sin(t * 3 + k);
      const g = ctx.createRadialGradient(ox, oy, 0, ox, oy, reach);
      const col = PALETTE[k % PALETTE.length];
      g.addColorStop(0, col + "55");
      g.addColorStop(1, col + "00");
      ctx.fillStyle = g;
      // два слоя: широкий мягкий ореол + узкое ядро — края луча не «рубленые»
      for (const [w, al] of [[2.2, 0.25], [1, 0.55]]) {
        ctx.globalAlpha = env * al * (col === "#ffffff" ? 0.5 : 1);
        ctx.beginPath();
        ctx.moveTo(ox, oy);
        ctx.arc(ox, oy, reach, ang - spread * w, ang + spread * w);
        ctx.closePath();
        ctx.fill();
      }
    }

    // 2. зайчики: вращаются вокруг шара, оставляя короткий шлейф
    const omega = 0.55;
    ctx.lineCap = "round";
    for (const p of spots) {
      if (t < p.born) continue;
      const a = p.a + t * omega;
      const twinkle = 0.55 + 0.45 * Math.sin(t * 9 + p.tw);
      const fadeIn = Math.min(1, (t - p.born) / 0.25);
      const x = ox + Math.cos(a) * p.d, y = oy + Math.sin(a) * p.d * 0.62;
      const tail = Math.min(0.08, 5 / p.d);
      const x2 = ox + Math.cos(a - tail) * p.d, y2 = oy + Math.sin(a - tail) * p.d * 0.62;
      ctx.globalAlpha = env * fadeIn * twinkle;
      ctx.strokeStyle = p.c;
      ctx.lineWidth = p.s * 2.6; ctx.globalAlpha *= 0.18;
      ctx.beginPath(); ctx.moveTo(x2, y2); ctx.lineTo(x, y); ctx.stroke(); // мягкое свечение
      ctx.globalAlpha = env * fadeIn * twinkle;
      ctx.lineWidth = p.s;
      ctx.beginPath(); ctx.moveTo(x2, y2); ctx.lineTo(x, y); ctx.stroke();
    }

    // 3. цветные шарики с гравитацией
    for (const s of sparks) {
      if (t > s.life) continue;
      s.vx *= 1 - 1.6 * dt; s.vy = s.vy * (1 - 1.6 * dt) + 380 * dt;
      s.x += s.vx * dt; s.y += s.vy * dt;
      ctx.globalAlpha = Math.max(0, 1 - t / s.life);
      ctx.fillStyle = s.c;
      ctx.beginPath(); ctx.arc(s.x, s.y, s.r, 0, TAU); ctx.fill();
    }

    // 4. вспышка в самом шаре
    if (t < 0.6) {
      const f = 1 - t / 0.6;
      const g = ctx.createRadialGradient(ox, oy, 0, ox, oy, 70);
      g.addColorStop(0, `rgba(255,255,255,${0.8 * f})`);
      g.addColorStop(1, "rgba(255,255,255,0)");
      ctx.globalAlpha = 1; ctx.fillStyle = g;
      ctx.fillRect(ox - 70, oy - 70, 140, 140);
    }

    raf = requestAnimationFrame(frame);
  };
  raf = requestAnimationFrame(frame);
}

/* ───────────────────────── Сама подпись ───────────────────────── */

const Sparkle = ({ className }) => (
  <svg className={`dt-credit__sparkle ${className}`} viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 0c.6 6.4 5.6 11.4 12 12-6.4.6-11.4 5.6-12 12-.6-6.4-5.6-11.4-12-12C6.4 11.4 11.4 6.4 12 0Z" />
  </svg>
);

export const DiscotechCredit = ({ className = "" }) => {
  const ballRef = useRef(null);
  const ballBtnRef = useRef(null);
  const [active, setActive] = useState(false);
  const [shineKey, setShineKey] = useState(0); // перезапуск блика
  const tapTimer = useRef(0);

  const enter = () => { setActive(true); setShineKey((k) => k + 1); ballRef.current?.spin(true); };
  const leave = () => { setActive(false); ballRef.current?.spin(false); };

  const party = () => {
    ballRef.current?.burst();
    setShineKey((k) => k + 1);
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (!reduce) launchParty(ballBtnRef.current);
    // на тач-устройствах нет hover — показываем эффект на секунду
    setActive(true);
    clearTimeout(tapTimer.current);
    tapTimer.current = setTimeout(() => {
      if (!ballBtnRef.current?.parentElement?.matches(":hover")) leave();
    }, 1600);
  };

  useEffect(() => () => { clearTimeout(tapTimer.current); activeParty?.(); }, []);

  return (
    <div
      className={`dt-credit ${active ? "is-active" : ""} ${className}`}
      onMouseEnter={enter}
      onMouseLeave={leave}
      onFocus={enter}
      onBlur={leave}
    >
      <button ref={ballBtnRef} type="button" className="dt-credit__ballbtn" onClick={party} aria-label="Включить дискотеку">
        <DiscoBall ref={ballRef} size={18} />
        <Sparkle className="dt-credit__sparkle--ball" />
      </button>

      <a
        href={STUDIO_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="dt-credit__link"
        onClick={party}
      >
        <span key={`t${shineKey}`} className="dt-credit__text">разработано</span>
        <span
          key={`l${shineKey}`}
          className="dt-credit__logo"
          role="img"
          aria-label="discotech"
          style={{ WebkitMaskImage: `url(${DiscotechLogo})`, maskImage: `url(${DiscotechLogo})` }}
        />
        <Sparkle className="dt-credit__sparkle--1" />
        <Sparkle className="dt-credit__sparkle--2" />
        <Sparkle className="dt-credit__sparkle--3" />
      </a>
    </div>
  );
};

export default DiscotechCredit;